import { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from './AuthContext';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from './ui/accordion';
import { ArrowLeft, CheckCircle, XCircle, Clock, Target } from 'lucide-react';
import { toast } from 'sonner';
import { MathJax } from 'better-react-mathjax';
import * as api from '../utils/api';
import { answersMatch } from '../utils/mathUtils';

// Use types from API
type Quiz = api.Quiz;
type Question = api.Question;
type QuizAttempt = api.Submission;

export default function QuizSummaryPage() {
  const { quizId } = useParams<{ quizId: string }>();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [quiz, setQuiz] = useState<Quiz | null>(null);
  const [attempt, setAttempt] = useState<QuizAttempt | null>(null);
  const [loading, setLoading] = useState(true);
  const [redirectTo, setRedirectTo] = useState<string | null>(null);

  // Handle navigation redirects
  useEffect(() => {
    if (redirectTo) {
      navigate(redirectTo);
    }
  }, [redirectTo, navigate]);

  useEffect(() => {
    loadData();
  }, [quizId, user]);

  const loadData = async () => {
    try {
      setLoading(true);

      if (!quizId || !user) return;

      // Load quiz data
      const quizData = await api.getQuizById(quizId);
      if (!quizData) {
        toast.error('Quiz not found');
        setRedirectTo('/student');
        return;
      }

      // Load attempt data
      const attemptData = await api.getSubmissionByQuiz(quizId);
      
      if (!attemptData) {
        toast.error('No submission found for this quiz');
        setRedirectTo('/student');
        return;
      }

      setQuiz(quizData);
      setAttempt(attemptData);
    } catch (error) {
      console.error('Error loading data:', error);
      toast.error('Failed to load quiz summary');
    } finally {
      setLoading(false);
    }
  };

  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}m ${secs}s`;
  };

  const getStudentAnswer = (questionId: string) => {
    return attempt?.answers[questionId] || '';
  };

  const isCorrect = (question: Question) => {
    const studentAnswer = getStudentAnswer(question.id) || '';
    return answersMatch(studentAnswer, question.correctAnswer?.answer || '');
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading summary...</p>
        </div>
      </div>
    );
  }

  if (!quiz || !attempt) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <p className="text-gray-600">Summary not available</p>
      </div>
    );
  }

  const percentage = attempt.totalPoints > 0 ? (attempt.score / attempt.totalPoints) * 100 : 0;
  const correctCount = quiz.questions.filter(q => isCorrect(q)).length;
  const incorrectCount = quiz.questions.length - correctCount;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-3xl text-gray-900">{quiz.title}</h2>
          <p className="text-gray-600 mt-1">{quiz.description}</p>
        </div>
        <Button variant="outline" onClick={() => navigate('/student')}>
          <ArrowLeft className="w-4 h-4 mr-2" />
          Back to Dashboard
        </Button>
      </div>

      {/* Score Summary */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-gray-600">Total Score</CardTitle>
            <Target className="w-4 h-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-gray-900">
              {attempt.score}/{attempt.totalPoints}
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {percentage.toFixed(1)}%
            </p>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-gray-600">Time Taken</CardTitle>
            <Clock className="w-4 h-4 text-gray-400" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-gray-900">
              {formatTime(attempt.timeSpent)}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-gray-600">Correct Answers</CardTitle>
            <CheckCircle className="w-4 h-4 text-green-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-gray-900 text-green-600">
              {correctCount}
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardHeader className="flex flex-row items-center justify-between pb-2">
            <CardTitle className="text-sm text-gray-600">Incorrect Answers</CardTitle>
            <XCircle className="w-4 h-4 text-red-600" />
          </CardHeader>
          <CardContent>
            <div className="text-2xl text-gray-900 text-red-600">
              {incorrectCount}
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Performance by Type */}
      <Card>
        <CardHeader>
          <CardTitle>Performance by Question Type</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {['mcq', 'short-answer'].map(type => {
              const questionsOfType = quiz.questions.filter(q => q.type === type);
              if (questionsOfType.length === 0) return null;
              
              const correctOfType = questionsOfType.filter(q => isCorrect(q)).length;
              const percentageOfType = (correctOfType / questionsOfType.length) * 100;

              return (
                <div key={type} className="p-4 bg-gray-50 rounded-lg">
                  <div className="flex items-center justify-between mb-2">
                    <span className="px-3 py-1 rounded-full text-xs font-medium capitalize bg-blue-100 text-blue-700">
                      {type === 'mcq' ? 'Multiple Choice' : 'Short Answer'}
                    </span>
                    <span className="text-sm text-gray-600">
                      {correctOfType}/{questionsOfType.length}
                    </span>
                  </div>
                  <div className="w-full bg-gray-200 rounded-full h-2">
                    <div
                      className="h-2 rounded-full bg-blue-600"
                      style={{ width: `${percentageOfType}%` }}
                    />
                  </div>
                  <p className="text-sm text-gray-600 mt-2">
                    {percentageOfType.toFixed(0)}% correct
                  </p>
                </div>
              );
            })}
          </div>
        </CardContent>
      </Card>

      {/* Question Review */}
      <Card>
        <CardHeader>
          <CardTitle>Question Review</CardTitle>
        </CardHeader>
        <CardContent>
          <Accordion type="single" collapsible className="w-full">
            {quiz.questions.map((question, index) => {
              const studentAnswer = getStudentAnswer(question.id);
              const correct = isCorrect(question);

              return (
                <AccordionItem key={question.id} value={question.id}>
                  <AccordionTrigger className="hover:no-underline">
                    <div className="flex items-center gap-3 w-full">
                      {correct ? (
                        <CheckCircle className="w-5 h-5 text-green-600 flex-shrink-0" />
                      ) : (
                        <XCircle className="w-5 h-5 text-red-600 flex-shrink-0" />
                      )}
                      <span className="text-left flex-1">
                        <MathJax inline>Question {index + 1}: {question.question.substring(0, 80)}{question.question.length > 80 ? '...' : ''}</MathJax>
                      </span>
                      <span className="ml-auto text-sm text-gray-500">
                        {question.points} pts
                      </span>
                    </div>
                  </AccordionTrigger>
                  <AccordionContent>
                    <div className="space-y-4 pt-4">
                      {/* Question Content */}
                      <div className="p-4 bg-gray-50 rounded-lg">
                        <p className="text-sm text-gray-600 mb-2">Question:</p>
                        <div className="prose max-w-none">
                          <MathJax dynamic>{question.question}</MathJax>
                        </div>
                      </div>

                      {/* Choices for MCQ */}
                      {question.type === 'mcq' && question.options && (
                        <div className="p-4 bg-gray-50 rounded-lg">
                          <p className="text-sm text-gray-600 mb-2">Choices:</p>
                          <div className="space-y-2">
                            {question.options.map((choice, i) => (
                              <div key={i} className="prose max-w-none">
                                <MathJax dynamic>{choice}</MathJax>
                              </div>
                            ))}
                          </div>
                        </div>
                      )}

                      {/* Your Answer */}
                      <div className={`p-4 rounded-lg ${correct ? 'bg-green-50' : 'bg-red-50'}`}>
                        <p className="text-sm font-medium mb-2">Your Answer:</p>
                        <div className="prose max-w-none">
                          <MathJax dynamic>
                            {studentAnswer 
                              ? (studentAnswer.includes('$$') ? studentAnswer : `$$${studentAnswer}$$`)
                              : '(No answer provided)'}
                          </MathJax>
                        </div>
                      </div>

                      {/* Correct Answer */}
                      <div className="p-4 bg-blue-50 rounded-lg">
                        <p className="text-sm font-medium mb-2">Correct Answer:</p>
                        <div className="prose max-w-none">
                          <MathJax dynamic>{question.correctAnswer?.answer || 'N/A'}</MathJax>
                        </div>
                      </div>

                      {/* Explanation */}
                      <div className="p-4 bg-indigo-50 rounded-lg">
                        <p className="text-sm font-medium mb-2">Explanation:</p>
                        <div className="prose max-w-none text-gray-700">
                          <MathJax dynamic>{question.correctAnswer?.explanation || 'No explanation available'}</MathJax>
                        </div>
                      </div>

                      {/* Points earned */}
                      <div className="p-4 bg-purple-50 rounded-lg">
                        <p className="text-sm font-medium">
                          Points: {correct ? question.points : 0} / {question.points}
                        </p>
                      </div>
                    </div>
                  </AccordionContent>
                </AccordionItem>
              );
            })}
          </Accordion>
        </CardContent>
      </Card>
    </div>
  );
}